import { useState } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { Contest, Team } from '../../models/types';
import { apiService, TeamMemberImportResult } from '../../services/api.service';
import { TOptions } from 'i18next';
import i18n from '../../i18n/i18n-setup';

// Snackbar text is built at notify time, so the module-level instance is enough here.
const t = (key: string, options: TOptions = {}): string => i18n.t(`contests:${key}`, options) as string;

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface UseContestActionsArgs {
  contest: Contest | null;
  hasEnded: boolean;
  canManage: boolean;
  refresh: () => void;
  navigate: NavigateFunction;
}

// Dialog state and mutations for the contest page (teams, members, participants, edit, delete).
export const useContestActions = ({ contest, hasEnded, canManage, refresh, navigate }: UseContestActionsArgs) => {
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [participantDialogOpen, setParticipantDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });

  const notify = (message: string, severity: SnackbarState['severity']) => setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  // Adds stay open while the contest runs so late joiners can be brought in; the server credits
  // their activities since the start. `action` is a key under contests:rosterActions.
  const guardRoster = (action: 'createTeams' | 'addMembers' | 'addParticipants'): boolean => {
    if (hasEnded) {
      notify(t('notify.rosterEnded', { action: t(`rosterActions.${action}`) }), 'warning');
      return false;
    }
    return true;
  };

  const openCreateTeam = () => {
    if (guardRoster('createTeams')) setTeamDialogOpen(true);
  };

  const openAddMember = (team: Team) => {
    if (!guardRoster('addMembers')) return;
    setSelectedTeam(team);
    setMemberDialogOpen(true);
  };

  const openImportMembers = () => {
    if (guardRoster('addMembers')) setImportDialogOpen(true);
  };

  // The import dialog makes its own requests; this only closes it and reports the outcome.
  const onMembersImported = (result: TeamMemberImportResult) => {
    setImportDialogOpen(false);
    refresh();
    if (result.failed.length === 0) {
      notify(t('notify.membersAdded', { count: result.added }), 'success');
    } else {
      notify(t('notify.membersPartial', { count: result.added, failed: result.failed.length }), 'warning');
    }
  };

  const openAddParticipant = () => {
    if (guardRoster('addParticipants')) setParticipantDialogOpen(true);
  };

  const closeMemberDialog = () => {
    setMemberDialogOpen(false);
    setSelectedTeam(null);
  };

  const saveTeam = async (teamData: { name: string }) => {
    if (!contest) return;
    try {
      setActionLoading(true);
      await apiService.createTeam(contest._id, teamData);
      setTeamDialogOpen(false);
      refresh();
      notify(t('notify.teamCreated'), 'success');
    } catch (error) {
      console.error('Error creating team:', error);
      notify(t('notify.teamCreateError'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const addTeamMember = async (memberData: { userId: string } | { userIds: string[] }) => {
    if (!selectedTeam) return;
    try {
      setActionLoading(true);
      if ('userIds' in memberData) {
        const result = await apiService.addMultipleMembersToTeam(selectedTeam._id, memberData.userIds);
        closeMemberDialog();
        refresh();
        if (result.failed.length === 0) {
          notify(t('notify.membersAdded', { count: result.successful.length }), 'success');
        } else {
          notify(t('notify.membersPartial', { count: result.successful.length, failed: result.failed.length }), 'warning');
        }
      } else {
        await apiService.addMemberToTeam(selectedTeam._id, memberData.userId);
        closeMemberDialog();
        refresh();
        notify(t('notify.memberAdded'), 'success');
      }
    } catch (error) {
      console.error('Error adding member(s):', error);
      notify(t('notify.memberAddError'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const addContestParticipant = async (data: { participantId: string } | { participantIds: string[] }) => {
    if (!contest) return;
    try {
      setActionLoading(true);
      if ('participantId' in data) {
        await apiService.addParticipantToContest(contest._id, data.participantId);
        setParticipantDialogOpen(false);
        refresh();
        notify(t('notify.participantAdded'), 'success');
      } else {
        const result = await apiService.addMultipleParticipantsToContest(contest._id, data.participantIds);
        setParticipantDialogOpen(false);
        refresh();
        const ok = result.successful.length;
        if (result.failed.length === 0) {
          notify(t('notify.participantsAdded', { count: ok }), 'success');
        } else if (ok === 0) {
          notify(t('notify.participantsAllFailed', { reason: result.failed[0]?.reason || t('notify.unknownError') }), 'error');
        } else {
          notify(t('notify.participantsPartial', { count: ok, failed: result.failed.length }), 'warning');
        }
      }
    } catch (error) {
      console.error('Error adding participant:', error);
      notify(error instanceof Error ? error.message : t('notify.participantAddError'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openEdit = () => {
    if (!canManage) {
      notify(t('notify.editAdminOnly'), 'warning');
      return;
    }
    setEditDialogOpen(true);
  };

  // Payload is whatever ContestForm emits; the API validates it.
  const saveContestEdit = async (contestData: Parameters<typeof apiService.updateContest>[1]) => {
    if (!contest) return;
    try {
      setActionLoading(true);
      await apiService.updateContest(contest._id, contestData);
      setEditDialogOpen(false);
      refresh();
      notify(t('notify.contestUpdated'), 'success');
    } catch (error) {
      console.error('Error updating contest:', error);
      notify(t('notify.contestUpdateError'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openDelete = () => {
    if (!canManage) {
      notify(t('notify.deleteAdminOnly'), 'warning');
      return;
    }
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!contest) return;
    try {
      setActionLoading(true);
      await apiService.deleteContest(contest._id);
      setDeleteDialogOpen(false);
      notify(t('notify.contestDeleted'), 'success');
      navigate(`/groups/${contest.groupId}`);
    } catch (error) {
      console.error('Error deleting contest:', error);
      notify(t('notify.contestDeleteError'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  return {
    snackbar,
    notify,
    closeSnackbar,
    actionLoading,
    selectedTeam,
    dialogs: {
      team: { open: teamDialogOpen, close: () => setTeamDialogOpen(false) },
      member: { open: memberDialogOpen, close: closeMemberDialog },
      importMembers: { open: importDialogOpen, close: () => setImportDialogOpen(false) },
      participant: { open: participantDialogOpen, close: () => setParticipantDialogOpen(false) },
      edit: { open: editDialogOpen, close: () => setEditDialogOpen(false) },
      remove: { open: deleteDialogOpen, close: () => setDeleteDialogOpen(false) },
    },
    openCreateTeam,
    openAddMember,
    openImportMembers,
    onMembersImported,
    openAddParticipant,
    openEdit,
    openDelete,
    saveTeam,
    addTeamMember,
    addContestParticipant,
    saveContestEdit,
    confirmDelete,
  };
};
